/**
 * SHA-256 of a file, computed in the browser before upload.
 *
 * Mirrors what the mobile app does at capture (src/infra/integrity/FileChecksum.ts):
 * the server recomputes the hash from the bytes it received and refuses the
 * upload when the two disagree, so a file corrupted or swapped on the way is
 * caught instead of being stored as sound evidence.
 *
 * Best effort on purpose. `crypto.subtle` is only available over HTTPS and on
 * localhost, so on a plain-HTTP deployment this returns null and the photo still
 * uploads — it is just recorded as having no origin hash, which is honest, and
 * far better than refusing to register the tree.
 */
export async function computeFileSha256(file: File): Promise<string | null> {
  if (typeof crypto === "undefined" || !crypto.subtle) return null;
  try {
    const buffer = await file.arrayBuffer();
    const digest = await crypto.subtle.digest("SHA-256", buffer);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
}
